import { motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'
import { useLanguage } from '../../contexts/LanguageContext'
import { ZoomIn } from 'lucide-react'

interface JourneyMemoriesProps {
  memories: string[]
  onImageClick: (image: string) => void
}

export function JourneyMemories({ memories, onImageClick }: JourneyMemoriesProps) {
  const { t } = useTranslation()
  const { isRTL } = useLanguage()
  
  if (!memories || memories.length === 0) {
    return null
  }

  return (
    <motion.div
      initial={{ opacity: 0, x: isRTL ? -50 : 50 }}
      whileInView={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.6, delay: 0.2 }}
      viewport={{ once: true }}
      className="bg-card border border-border rounded-xl p-4 sm:p-6 shadow-lg"
    >
      <h3 className="text-xl sm:text-2xl font-bold text-card-foreground mb-4 sm:mb-6">{t('home.journeyMemories')}</h3>
      <p className="text-muted-foreground text-sm mb-4 sm:mb-6">
        {t('home.reliveMoments')}
      </p>
      
      <div className="relative -mx-4 sm:-mx-6 overflow-hidden">
        <div 
          className={`flex gap-3 sm:gap-4 overflow-x-auto pb-4 px-4 sm:px-6 scrollbar-hide snap-x snap-mandatory ${isRTL ? 'rtl' : 'ltr'}`}
          style={{ 
            scrollbarWidth: 'none', 
            msOverflowStyle: 'none',
            WebkitOverflowScrolling: 'touch',
            direction: isRTL ? 'rtl' : 'ltr'
          } as React.CSSProperties}
        >
          {memories.map((image, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: index * 0.05 }}
              viewport={{ once: true }}
              className="relative overflow-hidden rounded-lg group cursor-pointer aspect-video flex-shrink-0 w-64 sm:w-72 md:w-80 snap-center"
              style={{ minWidth: '16rem' }}
              onClick={() => onImageClick(image)}
            >
              <img
                src={image}
                alt={`${t('home.journeyMemory')} ${index + 1}`}
                className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
              />
              <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors duration-300" />
              <div className="absolute bottom-2 left-2 text-white opacity-0 group-hover:opacity-100 transition-opacity duration-300 text-xs font-medium">
                Memory {index + 1}
              </div>
              <div className="absolute top-2 right-2 p-1 bg-black/50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <ZoomIn className="h-3 w-3 sm:h-4 sm:w-4 text-white" />
              </div>
            </motion.div>
          ))}
        </div>
        {/* Scroll indicators */}
        {memories.length > 1 && (
          <>
            {/* Left fade indicator */}
            <div className="absolute left-0 top-0 bottom-4 w-8 bg-gradient-to-r from-card to-transparent pointer-events-none z-10"></div>
            {/* Right fade indicator */}
            <div className="absolute right-0 top-0 bottom-4 w-8 bg-gradient-to-l from-card to-transparent pointer-events-none z-10"></div>
          </>
        )}
      </div>
    </motion.div>
  )
}
